import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import type { ImageObject } from '~/engine/data/image/ImageObject';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    countClearedSlicesWithContent,
    DeleteScopePlan,
    planKeepEveryNthSliceClears,
} from '~/engine/data/structure/deleteScopePlan';
import {
    isImageSlicePlane,
    resolveTextureSlice,
} from '~/engine/data/structure/planeSliceResolution';
import {
    type SliceAddress,
    sliceRefusalMessageId,
} from '~/hooks/structure-set/sliceRefusal';
import { useContourEditUndoOffer } from '~/hooks/undo/useContourEditUndoOffer';
import { contourWriteInFlightState } from '~/state/atoms/contourDeletionAtoms';
import {
    enqueueSnackbar,
    NOTIFICATION_AUTO_HIDE_MS,
} from '~/utils/notifications';

// ── Deleting a structure's contours over an extent ───────────────────
//
// The three extents share a write path that is deliberately NOT
// useRoiSdfOperation's: each clear goes through the undo-aware
// structure-set calls, which wrap it in `roi.startModify(name).finish()`
// — what writes the operation's own name into the ROI's undo history and
// the audit trail. Routing them through a texture replacement instead
// would rename all three and hand the ROI a computed texture rather than
// performing the clear they describe.
//
// What they DO share with every other operation is the busy → report
// contract. Without it a clear that throws inside the engine surfaces as
// nothing at all, leaving the clinician to infer from the viewport
// whether the structure emptied or nothing happened.

export type ClearExtentScope = 'this-slice' | 'all-slices' | 'keep-nth';

/**
 * Which slices an extent would clear, or `undefined` when the extent
 * cannot be planned at all.
 *
 * The single planner behind both the preview and the write, so a scope
 * cannot be re-derived differently between what the overlay showed and
 * what the commit did.
 */
export function buildDeleteScopePlan(
    scope: ClearExtentScope,
    texture: ContourTexture,
    image: ImageObject | undefined,
    plane: PlaneMm | null,
    nthValue: number
): DeleteScopePlan | undefined {
    if (scope === 'all-slices') {
        return { kind: 'whole-texture' };
    }
    if (scope === 'this-slice') {
        if (!image || plane === null) {
            return undefined;
        }
        const resolved = resolveTextureSlice(texture, image, plane);
        // A plane that addresses no slice of this ROI — outside its grid,
        // or cutting no image slice — is an empty plan, which previews as
        // "nothing would be deleted": the same no-op clearContoursOnSlice
        // performs.
        return {
            kind: 'slices',
            slices: resolved.kind === 'slice' ? [resolved.sliceZ] : [],
        };
    }
    // An ROI with no content anywhere has no cadence to thin and no
    // first/last slice to anchor: the planner says so with undefined, and
    // keepEveryNthSlice returns without clearing.
    const slices = planKeepEveryNthSliceClears(
        texture.signedDistanceField,
        nthValue
    );
    return { kind: 'slices', slices: slices ?? [] };
}

export interface ClearExtentMessages {
    /** The report. Formatted with `{name}` and `{slices}`. */
    successMessageId: string;
    failedMessageId: string;
    undoLabelId: string;
    undoUnavailableMessageId: string;
    logLabel: string;
}

interface ClearExtentRequestBase {
    /**
     * Whether the report carries an Undo. True where the clear commits
     * with no result checked first; false where the caller offered a
     * preview the user applied.
     */
    offerUndo: boolean;
}

/** A clear of one slice of the target. */
interface ClearSliceRequest extends ClearExtentRequestBase {
    scope: 'this-slice';
    /**
     * The plane the clear addresses. Resolved by the caller at the moment
     * of the click and passed as a value: the view can be scrolled or
     * turned in the same window, and a slice-addressed clear must never
     * land on the plane an earlier render saw. `null` clears nothing and
     * the report says why, rather than letting the write refuse.
     */
    plane: PlaneMm | null;
    /** How the caller named the plane, which the refusal words follow. */
    addressedBy: SliceAddress;
}

export type ClearExtentRequest =
    | ClearSliceRequest
    | (ClearExtentRequestBase & { scope: 'all-slices' })
    | (ClearExtentRequestBase & {
          scope: 'keep-nth';
          /** The retained cadence. */
          nthValue: number;
      });

/**
 * @param roiNumber the ROI to clear, for a caller that addresses one
 *   explicitly rather than the current selection — the structure list's
 *   row menu acts on the row it belongs to, which need not be selected.
 *   Taken here rather than per request because the Undo the report
 *   carries is keyed on it: an offer built for the selection would revert
 *   a different structure than the one the report names.
 */
export function useClearContourExtent(
    structureSetUid: string | undefined,
    messages: ClearExtentMessages,
    roiNumber?: number
) {
    const intl = useIntl();
    // The edit target, so the clear lands on the Environment the user is
    // editing.
    const { environment, roiNumber: selectedROI } = useActiveEditTarget();
    const targetRoi = roiNumber ?? selectedROI;
    const [isClearing, setIsClearingLocal] = useState(false);
    // Published globally as well as locally, so every surface that must
    // not start a second write reads one fact.
    const setWriteInFlight = useSetRecoilState(contourWriteInFlightState);
    const setIsClearing = useCallback(
        (value: boolean) => {
            setIsClearingLocal(value);
            setWriteInFlight(value);
        },
        [setWriteInFlight]
    );
    // See useClearCurvesInBox: the wording is fixed per call site and is
    // kept out of the handler's identity.
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    const undoOffer = useContourEditUndoOffer(structureSetUid, targetRoi, {
        undoLabelId: messages.undoLabelId,
        unavailableMessageId: messages.undoUnavailableMessageId,
    });

    const clearContourExtent = useCallback(
        async (request: ClearExtentRequest): Promise<void> => {
            const { scope, offerUndo } = request;
            const plane = request.scope === 'this-slice' ? request.plane : null;
            const nthValue =
                request.scope === 'keep-nth' ? request.nthValue : 0;
            if (!environment || targetRoi === -1) {
                // Reported, not skipped: the user asked for a clear and
                // would otherwise read silence as success.
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'clearContoursTargetUnavailable',
                    }),
                    { variant: 'error' }
                );
                return;
            }
            const roi = environment.structureSet?.getRoi(targetRoi);
            const roiName = roi?.name ?? '';
            const texture = roi?.texture;
            // The guard lives here, not at each call site: clearContoursOnSlice
            // THROWS on a plane that cuts no image slice, and every caller
            // would otherwise have to remember. A plane that no longer
            // addresses a slice clears nothing and is reported as such,
            // which is what the requirement asks for: refuse rather than
            // approximate.
            const planeToClear =
                plane &&
                environment.image &&
                isImageSlicePlane(environment.image, plane)
                    ? plane
                    : null;
            if (request.scope === 'this-slice' && planeToClear === null) {
                // Stated with the reason, in the same words the menu item
                // shows beside itself: a keystroke has no disabled control
                // to read, and "nothing was changed" would describe the
                // write while hiding why the slice was out of reach.
                enqueueSnackbar(
                    intl.formatMessage({
                        id: sliceRefusalMessageId({
                            tilted: !!environment.image?.parent.firstSlice
                                .tilted,
                            addressedBy: request.addressedBy,
                        }),
                    }),
                    { variant: 'warning' }
                );
                return;
            }

            setIsClearing(true);

            if (offerUndo) {
                // One live Undo at a time; the previous offer can no
                // longer act once this write lands.
                undoOffer.closePreviousOffer();
                // Before the write: the step this clear records does not
                // exist until the ROI's change has been published and
                // handled, so the baseline has to be the edit it is about
                // to supersede.
                undoOffer.captureStep();
            }

            try {
                // Counted on the LIVE texture immediately before the
                // write, so a scope that reached no slice reports THAT
                // rather than claiming a deletion.
                const plan = texture
                    ? buildDeleteScopePlan(
                          scope,
                          texture,
                          environment.image,
                          planeToClear,
                          nthValue
                      )
                    : undefined;
                const clearedSliceCount =
                    texture && plan
                        ? countClearedSlicesWithContent(
                              texture.signedDistanceField,
                              plan
                          )
                        : 0;

                if (scope === 'this-slice') {
                    if (planeToClear) {
                        environment.clearContourOnSlice(
                            targetRoi,
                            planeToClear
                        );
                    }
                } else if (scope === 'all-slices') {
                    environment.clearAllContours(targetRoi);
                } else {
                    environment.keepEveryNthSlice(targetRoi, nthValue);
                }

                enqueueSnackbar(
                    intl.formatMessage(
                        { id: messagesRef.current.successMessageId },
                        { name: roiName, slices: clearedSliceCount }
                    ),
                    {
                        variant: 'success',
                        // A clear that changed nothing banked no undo
                        // entry, so an Undo on its report would have
                        // nothing to revert and would decline.
                        ...(offerUndo && clearedSliceCount > 0
                            ? {
                                  autoHideDuration: NOTIFICATION_AUTO_HIDE_MS,
                                  action: undoOffer.action,
                              }
                            : {}),
                    }
                );
            } catch (error) {
                console.error(`${messagesRef.current.logLabel} error:`, error);
                enqueueSnackbar(
                    intl.formatMessage({
                        id: messagesRef.current.failedMessageId,
                    }),
                    { variant: 'error' }
                );
            } finally {
                setIsClearing(false);
            }
        },
        [environment, targetRoi, intl, undoOffer, setIsClearing]
    );

    return { clearContourExtent, isClearing };
}
